import type { ConcreteMedicalRecord, MedicalRecord, MedicalRecordPayload, StoredMedicalRecord } from "@qendo/medical";
import type { PermissionProps } from "@qendo/permissions";
import type { ListData } from "@qendo/types";
import { ucfirst } from "@samlevy/string-helpers";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { MedicalType, MedicalTypeNameMap } from "~/@types/qendo/medical/types";
import { resolveStoreMethod, useApi } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { fromRecord } from "~/util/qendo/medical";
import { medicalPermissionSection } from "~/util/qendo/permissions";

type MedicalTypeStore<T extends MedicalType = MedicalType> = Record<string, ConcreteMedicalRecord<T>>;

export type MedicalStore = {
    [MedicalType.allergy]: MedicalTypeStore<MedicalType.allergy>;
    [MedicalType.condition]: MedicalTypeStore<MedicalType.condition>;
    [MedicalType.immunisation]: MedicalTypeStore<MedicalType.immunisation>;
    [MedicalType.medication]: MedicalTypeStore<MedicalType.medication>;
    [MedicalType.past_symptom]: MedicalTypeStore<MedicalType.past_symptom>;
    [MedicalType.supplement]: MedicalTypeStore<MedicalType.supplement>;
    [MedicalType.surgery]: MedicalTypeStore<MedicalType.surgery>;
    [MedicalType.trigger]: MedicalTypeStore<MedicalType.trigger>;
};

declare type MedicalState = {
    loadingMedical: boolean;
    loadingMedicalError: boolean;
    listMedical<T extends MedicalType>(type: T): ConcreteMedicalRecord<T>[];
    findMedical<T extends MedicalType>(type: T, id: string): ConcreteMedicalRecord<T> | null;
    canUpdateMedical(type: MedicalType): boolean;
    storeMedical<T extends MedicalType>(payload: MedicalRecordPayload<T>): Promise<string | null>;
    destroyMedical<T extends MedicalType>(type: T, id: string): Promise<void>;
    refreshMedicalCache<T extends MedicalType>(type?: T): Promise<void>;
};

const medicalKeys = Object.values(MedicalType);

export const EmptyMedical = {
    allergies: {},
    conditions: {},
    immunisations: {},
    medications: {},
    symptoms: {},
    supplements: {},
    surgeries: {},
    triggers: {},
};

declare type DeleteMedicalPayload = {
    type: MedicalType;
    id: string;
};

export const MedicalContext = createContext<MedicalState>({
    loadingMedical: false,
    loadingMedicalError: false,
    listMedical: (_type) => [],
    findMedical: (_type, _id) => null,
    canUpdateMedical: (_type) => false,
    storeMedical: async (_payload) => null,
    destroyMedical: async (_type, _id) => {
        return;
    },
    refreshMedicalCache: async (_type) => {
        return;
    },
});

export function useMedical() {
    const value = useContext(MedicalContext);
    if (!value) {
        throw new Error("useMedical must be wrapped in a <MedicalProvider />");
    }
    return value;
}

export type MedicalProviderProps = {
    profile_id: string | null;
    children: ReactNode;
} & PermissionProps;

export function MedicalProvider({ profile_id, can, cannot, children }: MedicalProviderProps) {
    const queryClient = useQueryClient();
    const { client } = useApi();
    const { addToast } = useFeedback();

    const makeQueryKey = useCallback(<T extends MedicalType>(type: T) => ["medical", profile_id, type], [profile_id]);

    const refreshMedicalCache = async <T extends MedicalType>(type?: T) => {
        if (type) {
            const queryKey = makeQueryKey(type);

            await queryClient.invalidateQueries({ queryKey: queryKey, refetchType: "all" }).then(() => {
                queryClient.prefetchQuery({ queryKey: queryKey });
            });
        } else {
            await Promise.allSettled(Object.values(MedicalType).map((type) => refreshMedicalCache(type)));
        }
    };

    const canUpdateMedical = useCallback((type: MedicalType) => can("update", medicalPermissionSection(type)), [can]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(client): API
    const makeQueryByType = useCallback(
        <T extends MedicalType>(type: T) =>
            async (): Promise<Record<string, MedicalRecord<T>>> => {
                if (cannot("view", medicalPermissionSection(type))) {
                    return {};
                }

                return await client()
                    .get(`profiles/${profile_id}/${type}`)
                    .then((response: AxiosResponse<ListData<StoredMedicalRecord<T>>>) => {
                        return Object.fromEntries(
                            response.data.data.map((r) => {
                                const record = fromRecord(r);
                                return [record.id, record];
                            }),
                        );
                    })
                    .catch(() => ({}));
            },
        [profile_id, cannot],
    );

    const medicalQueries = useQueries({
        queries: profile_id
            ? medicalKeys.map((type) => ({
                  queryKey: makeQueryKey(type),
                  queryFn: makeQueryByType(type),
                  staleTime: 1000 * 60 * 60, // 1 hour,
              }))
            : [],
    });

    const records: MedicalStore = useMemo(
        () =>
            profile_id
                ? (Object.fromEntries(
                      medicalKeys.map((type, idx) => [type, medicalQueries[idx]?.data ?? {}]),
                  ) as MedicalStore)
                : EmptyMedical,
        [profile_id, medicalQueries],
    );

    const listMedical = useCallback(
        <T extends MedicalType>(type: T) => Object.values(records[type] ?? {}) as ConcreteMedicalRecord<T>[],
        [records],
    );

    const findMedical = useCallback(
        <T extends MedicalType>(type: T, id: string): ConcreteMedicalRecord<T> | null =>
            listMedical(type).find((m) => m.id === id) ?? null,
        [listMedical],
    );

    const storeMutation = useMutation<ConcreteMedicalRecord, Error, MedicalRecordPayload>({
        mutationFn: async ({ type, id, ...payload }: MedicalRecordPayload) => {
            if (!canUpdateMedical(type)) {
                throw new Error(`You do not have permission to update ${type} on this profile.`);
            }

            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/${type}`, id);

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<StoredMedicalRecord>) => {
                    const name = MedicalTypeNameMap[type];

                    addToast({
                        content: `${ucfirst(name)} ${response.status === 201 ? "added" : "updated"}`,
                    });

                    return fromRecord(response.data);
                });
        },
        onSuccess: (record) => {
            const { type, id } = record;
            const queryKey = makeQueryKey(type);

            queryClient.setQueryData(queryKey, (cache?: MedicalTypeStore) => {
                return {
                    ...cache,
                    [id]: record,
                };
            });
        },
    });

    const storeMedical = async <T extends MedicalType>(payload: MedicalRecordPayload<T>) => {
        const type = payload.type;

        if (!canUpdateMedical(type)) {
            return null;
        }

        return await storeMutation.mutateAsync(payload).then(({ id }) => id ?? null);
    };

    const destroyMutation = useMutation({
        mutationFn: async ({ type, id }: DeleteMedicalPayload) => {
            if (!canUpdateMedical(type)) {
                throw new Error(`You do not have permission to update ${type} on this profile.`);
            }

            return await client()
                .delete(`profiles/${profile_id}/${type}/${id}`)
                .then(() => {
                    const name = MedicalTypeNameMap[type];

                    addToast({
                        icon: "checkmark-circle",
                        content: `${ucfirst(name)} deleted`,
                        duration: 3000,
                    });

                    return { type, id };
                });
        },
        onSuccess: ({ type, id }: DeleteMedicalPayload) => {
            const queryKey = makeQueryKey(type);

            queryClient.setQueryData(queryKey, (cache?: MedicalTypeStore) => {
                const { [id]: _remove, ...rest } = cache ?? {};
                return rest;
            });
        },
        onSettled: async (_data, _error, { type }) => {
            await refreshMedicalCache(type);
        },
    });

    const destroyMedical = async <T extends MedicalType>(type: T, id: string) => {
        if (!canUpdateMedical(type)) {
            return;
        }

        await destroyMutation.mutateAsync({ type, id });
    };

    const value = {
        loadingMedical: medicalQueries.some((q) => q.isLoading || q.isPending),
        loadingMedicalError: medicalQueries.some((q) => q.isError || q.isLoadingError),
        listMedical,
        findMedical,
        canUpdateMedical,
        storeMedical,
        destroyMedical,
        refreshMedicalCache,
    };

    return <MedicalContext.Provider value={value}>{children}</MedicalContext.Provider>;
}
